import { HeaderBidder } from '@tunein/web-common';
import { pageLifeCycleWatcher } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { forEach } from 'es-toolkit/compat';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { ADS_PREROLL_CAP_COUNT_ENABLED, ADS_SMART_PREROLL_ENABLED } from '../constants';
import { appSessionDataProvider } from '../providers';
import { api, player } from '../singletons';
import { debug } from '../utils';
import { buildAdsWizzAdTagUrl, buildCommonCustomParams, buildGoogleIMAAdTagUrl, } from '../utils/adTagBuilder';
import { AdReporter } from './AdReporter';
import { providerToAdNetworkMap } from './AdReporter/constants';
import { APS_BIDS_RESPONSE_LOGGING_ENABLED, adTypes, CAP_KEY_BY_MEDIUM, clientAdTypes, FREESTAR_ENABLED, GUARANTEE_MIDROLL, GUARANTEE_MIDROLL_CHROMECAST, GUARANTEE_PREROLL, GUARANTEE_PREROLL_CHROMECAST, MIDROLL_ENABLED, PREROLL_ENABLED, VIDEO_PREROLL_ENABLED, } from './constants';
import { MidrollScheduler } from './MidrollScheduler';
export class AdPlayer extends EventEmitter {
    #adReporter;
    #streamDataManager;
    #midrollScheduler;
    #playerEvents;
    #sessionConfig;
    #headerBidder;
    #didVideoPrerollPlay = false;
    #isReturningFromBoost = false;
    #adBreakSessionId = null;
    #currentAdMedium = null;
    // Counts ads that actually started during the current client-side preroll break.
    // We use this across the video/audio request sequence for preroll caps and to
    // determine whether anything in the break played at all.
    #startedPrerollsCount = 0;
    constructor(options) {
        super();
        const { ssaiManager, streamDataManager } = options;
        this.#streamDataManager = streamDataManager;
        this.#playerEvents = {
            playing: this.#onPlaying,
            pause: this.#onPause,
            emptied: this.#onStopped,
            ended: this.#onStopped,
            waiting: this.#onPause,
            error: this.#onSourceError,
        };
        this.#adReporter = new AdReporter({ ssaiManager });
        this.#headerBidder = new HeaderBidder(api.getPrebiddingConfig(clientAdTypes.preroll));
        this.#registerPlayerEvents();
        api.on('postAuthCompleted', this.#onPostAuthCompleted);
        appSessionDataProvider.on('consentDataUpdated', this.#onConsentDataUpdated);
        this.#init();
    }
    #onConsentDataUpdated = async (consentData) => {
        await this.#headerBidder.ready;
        this.#headerBidder.updateConsent(consentData);
    };
    get #currentStream() {
        return (player.currentSource || {});
    }
    get #nowPlayingAdsSettings() {
        const { nowPlaying } = this.#sessionConfig;
        return nowPlaying?.ads || {};
    }
    get #canPlayPreroll() {
        if (!this.canPlayClientSideAds) {
            return false;
        }
        const { canShowAds } = this.#nowPlayingAdsSettings;
        const canPlayAudioOrVideoPreroll = this.#canPlayAudioPreroll || this.#canPlayVideoPreroll;
        const canPlayPreroll = canShowAds && canPlayAudioOrVideoPreroll && api.getConfigSetting(PREROLL_ENABLED);
        // QA controls. See constant definitions for more details.
        const forcePreroll = api.getConfigSetting(GUARANTEE_PREROLL) || api.getConfigSetting(GUARANTEE_PREROLL_CHROMECAST);
        return Boolean(canPlayPreroll || forcePreroll);
    }
    get #canPlayAudioPreroll() {
        const { canShowPrerollAds } = this.#nowPlayingAdsSettings;
        const audioAdUnitId = api.getAdUnitConfig('preroll', 'audio')?.adUnitId;
        if (!audioAdUnitId) {
            this.#adReporter.reportNoAdConfig();
        }
        return Boolean(audioAdUnitId && canShowPrerollAds);
    }
    get #canPlayVideoPreroll() {
        const videoAdUnitId = api.getAdUnitConfig('preroll', 'video')?.adUnitId;
        const { canShowVideoPrerollAds } = this.#nowPlayingAdsSettings;
        if (!videoAdUnitId) {
            this.#adReporter.reportNoAdConfig();
        }
        return Boolean(videoAdUnitId && api.getConfigSetting(VIDEO_PREROLL_ENABLED) && canShowVideoPrerollAds);
    }
    get #canPlayMidroll() {
        if (!this.canPlayClientSideAds) {
            return false;
        }
        const { canShowAds, canShowPrerollAds } = this.#nowPlayingAdsSettings;
        const midrollAdUnitId = api.getAdUnitConfig('midroll', 'audio')?.adUnitId;
        // Give precedence to the adConfig and player status over the nowPlaying data and config settings
        if (!midrollAdUnitId || !player.isPlaying()) {
            return false;
        }
        const canPlayMidroll = canShowAds &&
            canShowPrerollAds && // the NowPlaying API doesn't have a `canShowMidrollAds`, so we rely on `canShowPrerollAds`
            api.getConfigSetting(MIDROLL_ENABLED) &&
            this.#currentStream.isMidrollEnabled &&
            !this.#currentStream.isSSAI; // SSAI streams embed their own instream midrolls
        return Boolean(canPlayMidroll ||
            api.getConfigSetting(GUARANTEE_MIDROLL) ||
            api.getConfigSetting(GUARANTEE_MIDROLL_CHROMECAST));
    }
    async #init() {
        pageLifeCycleWatcher.init();
        if (this.canPlayClientSideAds) {
            await appSessionDataProvider.ready;
            player.setOptions({ advertising: appSessionDataProvider.adOptions });
            // Fire-and-forget: sub-bidders self-gate their fetch path on init
            // readiness, so preroll requests can proceed without waiting for the
            // APS/Magnite iframe handshakes to complete.
            this.#initializeHeaderBidder();
        }
    }
    #registerPlayerEvents() {
        forEach(this.#playerEvents, (cb, event) => {
            player.on(event, cb);
        });
    }
    #deregisterPlayerEvents() {
        forEach(this.#playerEvents, (cb, event) => player.off(event, cb));
    }
    #registerAdsEvents() {
        player.on('adimpression', this.#onAdImpression);
        const { prerollTimeCapDuration, prerollCountCapEnabled } = this.#getAdsLimits();
        // Preroll time capping
        if (prerollTimeCapDuration > 0) {
            player.on('adprogress', this.#onAdProgress);
        }
        // Preroll count capping
        if (prerollCountCapEnabled) {
            player.on('adloaded', this.#onAdLoaded);
        }
    }
    #deregisterAdsEvents() {
        player.off('adprogress', this.#onAdProgress);
        player.off('adloaded', this.#onAdLoaded);
        player.off('adimpression', this.#onAdImpression);
    }
    #onPlaying = () => {
        this.#midrollScheduler?.start();
    };
    #onPause = () => {
        this.#midrollScheduler?.pause();
    };
    #onStopped = () => {
        this.#midrollScheduler?.clear();
    };
    #onSourceError = (event) => {
        if (event.error.fatal) {
            this.#midrollScheduler?.clear();
        }
    };
    #onPostAuthCompleted = async () => {
        if (!this.canPlayClientSideAds) {
            this.#headerBidder.stopVideoBidsPolling();
            return;
        }
        await appSessionDataProvider.ready;
        player.setOptions({ advertising: appSessionDataProvider.adOptions });
        this.#initializeHeaderBidder();
    };
    #onAdProgress = (event) => {
        const { currentTime } = event;
        const { prerollTimeCapDuration } = this.#getAdsLimits();
        if (!currentTime || !this.#currentAdMedium || prerollTimeCapDuration <= 0) {
            return;
        }
        if (currentTime > prerollTimeCapDuration) {
            player.forceSkipAd();
            this.#adReporter.reportPlaybackCancelled({ duration: currentTime, reason: 'time' });
        }
    };
    #onAdLoaded = () => {
        const { maxTotalPrerollAds, prerollCountCapEnabled } = this.#getAdsLimits();
        // We are checking startedPrerollsCount to be larger or EQUAL because impression is triggered after adloaded event
        if (prerollCountCapEnabled && this.#startedPrerollsCount >= maxTotalPrerollAds) {
            // Note: It would be better to call here adsManager.discardAdBreak() but it has no effect on AdsWizz ads (reported)
            player.forceSkipAd();
            this.#adReporter.reportPlaybackCancelled({ reason: 'count' });
        }
    };
    #onAdImpression = () => {
        this.#startedPrerollsCount += 1;
    };
    #setAdBreak(isAdBreak) {
        player.isAdBreak = isAdBreak;
        this.#streamDataManager.setOutstreamAdBreak(isAdBreak, isAdBreak ? (this.#currentAdMedium ?? undefined) : undefined);
    }
    #emitAdBreakEvent(event, data) {
        this.emit(event, data);
    }
    #emitAdSlotEvent(event, data) {
        this.emit(event, data);
    }
    #registerHeaderBidderEvents() {
        this.#headerBidder.off('bidRequested', this.#adReporter.reportAudioVideoAdsBiddingRequested);
        this.#headerBidder.off('bidResponseReceived', this.#adReporter.reportAudioVideoAdsBiddingResponseReceived);
        this.#headerBidder.off('bidRequestFailed', this.#adReporter.reportAudioVideoAdsBiddingRequestFailed);
        this.#headerBidder.on('bidRequested', this.#adReporter.reportAudioVideoAdsBiddingRequested);
        this.#headerBidder.on('bidResponseReceived', this.#adReporter.reportAudioVideoAdsBiddingResponseReceived);
        this.#headerBidder.on('bidRequestFailed', this.#adReporter.reportAudioVideoAdsBiddingRequestFailed);
    }
    async #initializeHeaderBidder() {
        const isFreestarEnabled = api.getConfigSetting(FREESTAR_ENABLED, false);
        this.#registerHeaderBidderEvents();
        await this.#headerBidder.init({
            useApsIframe: isFreestarEnabled,
            consentData: appSessionDataProvider.consentData,
        });
        // Pre-warm the video bid cache so the first preroll request doesn't block
        // on the first fetch. Gated on presence of any configured partner in the
        // partner ads config.
        if (api.getPrebiddingConfig(clientAdTypes.preroll)) {
            this.#headerBidder.pollAndCacheVideoBids();
        }
    }
    async #waitForServerSidePrerollEnd() {
        const { promise, resolve } = Promise.withResolvers();
        function resolvePrerollPromise(didPlayPreroll = false) {
            resolve(didPlayPreroll);
            player.off('ended', onEnded);
            player.off('error', onError);
            player.off('loadstart', onLoadStart);
        }
        function onEnded() {
            resolvePrerollPromise(true);
        }
        function onError(event) {
            if (!(event.error?.fatal && event.isLastSource)) {
                return;
            }
            resolvePrerollPromise();
        }
        function onLoadStart() {
            resolvePrerollPromise();
        }
        player.on('ended', onEnded);
        player.on('error', onError);
        player.on('loadstart', onLoadStart);
        return promise;
    }
    async #requestServerSidePreroll() {
        const wasAutoPlayEnabled = player.getOptions().autoPlay;
        // disable autoplay so that we can send ad reports at the appropriate times
        if (wasAutoPlayEnabled) {
            player.setOptions({ autoPlay: false });
        }
        const adBreakSessionId = this.#adBreakSessionId;
        this.#deregisterPlayerEvents();
        this.#adReporter.registerEvents();
        this.#adReporter.reportServerSidePrerollRequested();
        await player.load(this.#sessionConfig.prerollStream);
        if (adBreakSessionId !== this.#adBreakSessionId) {
            throw new Error('Ad break interrupted by other process');
        }
        if (!player.isLoaded()) {
            this.#registerPlayerEvents();
            this.#adReporter.deregisterEvents();
            if (wasAutoPlayEnabled) {
                player.setOptions({ autoPlay: true });
            }
            return false;
        }
        const prerollEndPromise = this.#waitForServerSidePrerollEnd();
        this.#adReporter.reportServerSidePrerollResponseReceived();
        player.play();
        const didAdPlay = await prerollEndPromise;
        if (adBreakSessionId !== this.#adBreakSessionId) {
            throw new Error('Ad break interrupted by other process');
        }
        this.#registerPlayerEvents();
        this.#adReporter.deregisterEvents();
        if (wasAutoPlayEnabled) {
            player.setOptions({ autoPlay: true });
        }
        return didAdPlay;
    }
    #generateAdTagUrl(options) {
        const { adUnitId, medium, provider, maxAdsCount, companionSizes = '', adType, videoApsBids, } = options;
        const { guideId, nowPlaying, sessionAdTagParams } = this.#sessionConfig;
        const { videoIsFirstInSession, audioIsFirstInSession } = sessionAdTagParams;
        const { consentData } = appSessionDataProvider;
        const { isTargetingEnabled, gdprApplies, gdprTcString, usPrivacyString } = consentData;
        const commonRootParams = {
            us_privacy: usPrivacyString,
            ...(api.isInUS && { rdp: isTargetingEnabled ? '0' : '1' }),
        };
        const isVideoAd = medium === 'video';
        const commonCustParams = buildCommonCustomParams({
            videoAdPlayed: this.#didVideoPrerollPlay,
            isFirstInSession: isVideoAd ? videoIsFirstInSession : audioIsFirstInSession,
        });
        if (provider === 'google') {
            const isSmartPrerollEnabled = api.getConfigSetting(ADS_SMART_PREROLL_ENABLED);
            const url = `https://tunein.com/desc/${guideId}/`;
            // Player will initially return undefined for volume, so we default to 1 (unmuted ads)
            const volume = player.getVolume() === undefined ? 1 : player.getVolume();
            const baseParams = {
                ...commonRootParams,
                url,
                iu: adUnitId,
                description_url: url,
                scor: guideId?.slice(1),
                vpmute: volume ? '0' : '1',
                npa: isTargetingEnabled ? '0' : '1',
                gdpr: gdprApplies ? '1' : '0',
                ciu_szs: companionSizes,
                pp: api.adsTargeting?.ads_partner_alias,
                us_privacy: usPrivacyString,
                ...(gdprApplies && gdprTcString && { gdpr_consent: gdprTcString }),
                cust_params: {
                    ...commonCustParams,
                    ...nowPlaying?.adTargeting,
                    partnerId: api.partnerId,
                    us_privacy: usPrivacyString,
                    ...(isSmartPrerollEnabled
                        ? { MaxReqAds: maxAdsCount.toString() }
                        : {
                            enableDoublePreroll: Boolean(this.#getAdsLimits().canShowDoublePrerollAds).toString(),
                        }),
                },
            };
            if (isVideoAd &&
                videoApsBids?.adParams &&
                api.getConfigSetting(APS_BIDS_RESPONSE_LOGGING_ENABLED)) {
                api.telemetry.reportSandbox({
                    category: 'aps_bids',
                    action: 'video_preroll',
                    props: {
                        bids: JSON.stringify(videoApsBids.adParams),
                    },
                });
            }
            return buildGoogleIMAAdTagUrl({
                baseParams,
                isVideoAd,
                videoApsBids,
            });
        }
        if (provider === 'adswizz') {
            const baseParams = {
                ...commonRootParams,
                partnerId: api.partnerId,
                maxAds: maxAdsCount.toString(),
                'aw_0_1st.playerid': api.partnerId,
                ...(gdprApplies && {
                    'aw_0_req.userConsentV2': gdprTcString,
                    'aw_0_req.gdpr': Boolean(isTargetingEnabled).toString(),
                }),
            };
            const customParams = {
                ...commonCustParams,
                ...nowPlaying?.adTargeting,
            };
            return buildAdsWizzAdTagUrl({
                baseParams,
                customParams,
                companionSizes,
                // AdsWizz tag builder currently only has URL mappings for preroll/midroll
                adType: adType,
            });
        }
        return '';
    }
    async #requestAdBreak({ maxAdsCount, adType, medium }) {
        let playedAdsCount = 0;
        const adRequestId = uuidv4();
        const { adUnitId, provider, companionSizes } = api.getAdUnitConfig(adType, medium) || {};
        if (!adUnitId || !provider) {
            this.#adReporter.reportNoAdConfig(adRequestId);
            return 0;
        }
        const adBreakSessionId = this.#adBreakSessionId;
        const { primaryGuideId, secondaryGuideId } = this.#sessionConfig?.nowPlaying;
        const isVideoAd = medium === 'video';
        const videoApsBids = isVideoAd ? this.#headerBidder.consumeVideoBidsAdParams() : undefined;
        this.#deregisterPlayerEvents();
        this.#adReporter.registerEvents();
        this.#registerAdsEvents();
        const adRequestUrl = this.#generateAdTagUrl({
            adUnitId,
            medium,
            provider,
            maxAdsCount,
            companionSizes,
            adType,
            videoApsBids,
        });
        debug('[AdPlayer] Generated ad tag URL:', adRequestUrl);
        this.#adReporter.onAdRequested({
            type: adTypes.client,
            clientType: adType,
            adUnitId,
            adRequestId,
            guideIds: [primaryGuideId, secondaryGuideId].filter(Boolean).join(','),
            adNetworkName: providerToAdNetworkMap[provider],
            medium,
            biddingNetworks: videoApsBids?.bidRequests?.map(({ requestId, network, hasBid }) => ({
                requestId,
                networkName: network,
                isIncluded: hasBid,
            })),
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
            adRequestUrl,
        });
        // isFirstInSession is attached to the ad request url, so we should reset it now
        if (isVideoAd) {
            this.#sessionConfig.sessionAdTagParams.videoIsFirstInSession = false;
        }
        else {
            this.#sessionConfig.sessionAdTagParams.audioIsFirstInSession = false;
        }
        try {
            this.#adReporter.reportAudioVideoAdRequested();
            const adBreakPromise = player.requestAdBreak(provider, adRequestId, adRequestUrl);
            if (isVideoAd && api.getPrebiddingConfig(clientAdTypes.preroll)) {
                // Use the ready snapshot for the current ad request, then warm the
                // next provider snapshots in the background without blocking playback.
                this.#headerBidder.pollAndCacheVideoBids();
            }
            playedAdsCount = await adBreakPromise;
            if (adBreakSessionId !== this.#adBreakSessionId) {
                return;
            }
            if (!playedAdsCount) {
                debug(`[AdPlayer] No-fill for ${medium} ad break. Requested ${maxAdsCount} ads.`);
                this.#adReporter.reportNoFill(adRequestId);
            }
        }
        catch (error) {
            if (adBreakSessionId !== this.#adBreakSessionId) {
                return;
            }
            debug(`[AdPlayer] Error requesting ${medium} ad break:`, error);
            // AdErrorEvents are handled by AdReporter
            if (!error.isAdError) {
                this.#adReporter.reportAdError(error, adRequestId);
            }
        }
        if (adBreakSessionId !== this.#adBreakSessionId) {
            return;
        }
        this.#adReporter.deregisterEvents();
        this.#registerPlayerEvents();
        this.#deregisterAdsEvents();
        if (playedAdsCount && isVideoAd) {
            this.#didVideoPrerollPlay = true;
        }
        return playedAdsCount;
    }
    #getAdsLimits() {
        const { maxSwitchAudioPrerollAds = 1, maxTotalPrerollAds = 3, maxVideoPrerollAds = 2, canShowDoublePrerollAds, } = this.#nowPlayingAdsSettings;
        const isSmartPrerollEnabled = api.getConfigSetting(ADS_SMART_PREROLL_ENABLED);
        const prerollCountCapEnabled = api.getConfigSetting(ADS_PREROLL_CAP_COUNT_ENABLED, false);
        const prerollTimeCapDuration = this.#currentAdMedium
            ? api.getConfigSetting(CAP_KEY_BY_MEDIUM[this.#currentAdMedium], 0)
            : 0;
        // TODO: remove this once we're ready to permanently enable smart prerolls
        if (!isSmartPrerollEnabled) {
            return {
                maxTotalPrerollAds: canShowDoublePrerollAds ? 3 : 2,
                maxVideoPrerollAds: 1,
                canShowDoublePrerollAds,
                prerollTimeCapDuration,
                prerollCountCapEnabled,
            };
        }
        return {
            maxTotalPrerollAds: this.#isReturningFromBoost
                ? maxSwitchAudioPrerollAds
                : maxTotalPrerollAds,
            maxVideoPrerollAds: this.#isReturningFromBoost ? 0 : maxVideoPrerollAds,
            prerollTimeCapDuration,
            prerollCountCapEnabled,
        };
    }
    async #requestPreroll() {
        const { maxTotalPrerollAds, maxVideoPrerollAds } = this.#getAdsLimits();
        const adBreakSessionId = this.#adBreakSessionId;
        if (pageLifeCycleWatcher.isVisible) {
            await this.#requestVideoPreroll(maxVideoPrerollAds);
        }
        if (adBreakSessionId !== this.#adBreakSessionId) {
            throw new Error('Ad break interrupted by other process');
        }
        const maxAudioPrerollAds = Math.max(maxTotalPrerollAds - this.#startedPrerollsCount, 0);
        // It's OK to pass 0 to serve audio preroll because of ad reporting.
        await this.#requestAudioPreroll(maxAudioPrerollAds);
        if (adBreakSessionId !== this.#adBreakSessionId) {
            throw new Error('Ad break interrupted by other process');
        }
        return Boolean(this.#startedPrerollsCount);
    }
    async #requestAudioPreroll(maxAdsCount) {
        const { canShowPrerollAds } = this.#nowPlayingAdsSettings;
        this.#currentAdMedium = 'audio';
        this.#adReporter.reportAudioVideoPrerollEligibility({
            medium: 'audio',
            isPlatformEligible: canShowPrerollAds,
            isEligible: maxAdsCount > 0,
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
        });
        if (!this.#canPlayAudioPreroll || maxAdsCount <= 0) {
            return;
        }
        const slotEventData = {
            medium: 'audio',
            type: 'client',
            adType: clientAdTypes.preroll,
        };
        this.#emitAdSlotEvent('adSlotStarted', slotEventData);
        const playedAdsCount = await this.#requestAdBreak({
            maxAdsCount,
            adType: clientAdTypes.preroll,
            medium: 'audio',
        });
        this.#emitAdSlotEvent('adSlotEnded', slotEventData);
        return playedAdsCount;
    }
    async #requestVideoPreroll(maxAdsCount) {
        const { canShowVideoPrerollAds } = this.#nowPlayingAdsSettings;
        this.#currentAdMedium = 'video';
        this.#adReporter.reportAudioVideoPrerollEligibility({
            medium: 'video',
            isPlatformEligible: canShowVideoPrerollAds,
            isEligible: this.#canPlayVideoPreroll && maxAdsCount > 0,
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
        });
        if (!this.#canPlayVideoPreroll || maxAdsCount <= 0) {
            return;
        }
        const slotEventData = {
            medium: 'video',
            type: 'client',
            adType: clientAdTypes.preroll,
        };
        this.#emitAdSlotEvent('adSlotStarted', slotEventData);
        const playedAdsCount = await this.#requestAdBreak({
            maxAdsCount,
            adType: clientAdTypes.preroll,
            medium: 'video',
        });
        this.#emitAdSlotEvent('adSlotEnded', slotEventData);
        return playedAdsCount;
    }
    #requestMidroll = async () => {
        if (!this.#canPlayMidroll) {
            return;
        }
        debug('[AdPlayer] Requesting midroll ad break');
        const adBreakSessionId = this.#adBreakSessionId;
        const breakEventData = {
            type: 'client',
            adType: clientAdTypes.midroll,
        };
        const slotEventData = {
            ...breakEventData,
            medium: 'audio',
        };
        this.#emitAdBreakEvent('adBreakStarted', breakEventData);
        this.#emitAdSlotEvent('adSlotStarted', slotEventData);
        const playedAdsCount = await this.#requestAdBreak({
            maxAdsCount: 1,
            adType: clientAdTypes.midroll,
            medium: 'audio',
        }).catch(noop);
        this.#emitAdSlotEvent('adSlotEnded', slotEventData);
        if (adBreakSessionId !== this.#adBreakSessionId) {
            this.#emitAdBreakEvent('adBreakEnded', breakEventData);
            return;
        }
        if (!playedAdsCount) {
            this.#emitAdBreakEvent('adBreakEnded', breakEventData);
            return this.#midrollScheduler?.start();
        }
        this.#emitAdBreakEvent('adBreakEnded', breakEventData);
        // TODO: expose a method on WMP that indicates if the ad player is sharing a media element with the content
        //  player so that we can conditionally call load()
        await player.load();
        if (player.isLoaded()) {
            await player.play();
        }
    };
    get canPlayClientSideAds() {
        return !api.isPremium && appSessionDataProvider.hasAdConfig;
    }
    get canPlayPreroll() {
        return this.#canPlayPreroll;
    }
    setSessionConfig(sessionConfig) {
        this.#sessionConfig = {
            ...this.#sessionConfig,
            ...sessionConfig,
            sessionAdTagParams: {
                ...this.#sessionConfig?.sessionAdTagParams,
                ...sessionConfig?.sessionAdTagParams,
                videoIsFirstInSession: true,
                audioIsFirstInSession: true,
            },
        };
        this.#setAdBreak(false);
        this.#adBreakSessionId = uuidv4();
        this.#adReporter.deregisterEvents();
        this.#midrollScheduler?.clear();
        this.#midrollScheduler = new MidrollScheduler({
            requestMidroll: this.#requestMidroll,
        });
        this.#adReporter.reportServerSidePrerollEligibility(this.#sessionConfig.prerollStream);
    }
    async requestPreroll(options) {
        debug('[AdPlayer] Requesting preroll ad break');
        this.#currentAdMedium = null;
        this.#startedPrerollsCount = 0;
        player.didPlayPreroll = false;
        this.#setAdBreak(true);
        await appSessionDataProvider.ready;
        if (this.#sessionConfig?.prerollStream) {
            const breakEventData = {
                type: 'server',
                adType: clientAdTypes.preroll,
            };
            this.#emitAdBreakEvent('adBreakStarted', breakEventData);
            const slotEventData = {
                ...breakEventData,
                medium: 'audio',
            };
            this.#emitAdSlotEvent('adSlotStarted', slotEventData);
            try {
                player.didPlayPreroll = await this.#requestServerSidePreroll();
            }
            catch {
                this.#emitAdSlotEvent('adSlotEnded', slotEventData);
                this.#emitAdBreakEvent('adBreakEnded', breakEventData);
                // do nothing
                return;
            }
            if (player.didPlayPreroll) {
                this.#adReporter.reportAdBreakCompleted();
            }
            this.#emitAdSlotEvent('adSlotEnded', slotEventData);
            this.#emitAdBreakEvent('adBreakEnded', breakEventData);
            this.#setAdBreak(false);
            return;
        }
        const breakEventData = {
            type: 'client',
            adType: clientAdTypes.preroll,
        };
        this.#emitAdBreakEvent('adBreakStarted', breakEventData);
        this.#isReturningFromBoost = options?.isReturningFromBoost || false;
        try {
            player.didPlayPreroll = await this.#requestPreroll();
            this.#emitAdBreakEvent('adBreakEnded', breakEventData);
            this.#setAdBreak(false);
        }
        catch {
            this.#emitAdBreakEvent('adBreakEnded', breakEventData);
        }
    }
    resetState() {
        this.#adBreakSessionId = null;
        player.didPlayPreroll = false;
        this.#setAdBreak(false);
    }
}
